import { type CSSProperties, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_DISPLAY_TIMEZONE,
  type CloudToDashboardMessage,
  type DashboardChannelInfo,
} from '@timer-manager/shared';
import { Clock } from './components/Clock.tsx';
import { OnAirBadge } from './components/OnAirBadge.tsx';
import { CountdownCard, getUrgency } from './components/CountdownCard.tsx';
import { SourceIndicator } from './components/SourceIndicator.tsx';
import { StatusIndicator } from './components/StatusIndicator.tsx';
import { ChannelLogo } from './components/ChannelLogo.tsx';
import { useWebSocket } from './hooks/useWebSocket.ts';
import {
  makeAnchor,
  useInterpolatedTiming,
  type TimingAnchor,
} from './hooks/useInterpolatedTiming.ts';
import { useCarouselPhase, CAROUSEL_SWIPE_MS } from './hooks/useCarouselPhase.ts';
import { deriveLeftSlot, deriveRightSlot, type CardSpec } from './utils/cards.ts';

// ---------------------------------------------------------------------------
// Slug extraction
// ---------------------------------------------------------------------------

function useSlug(): string | null {
  const [slug, setSlug] = useState(() => parseSlug(window.location.pathname));
  useEffect(() => {
    const listener = (): void => setSlug(parseSlug(window.location.pathname));
    window.addEventListener('popstate', listener);
    return () => window.removeEventListener('popstate', listener);
  }, []);
  return slug;
}

function parseSlug(pathname: string): string | null {
  const m = /^\/dashboard\/([a-z0-9-]+)\/?$/.exec(pathname);
  return m?.[1] ?? null;
}

function buildWsUrl(slug: string): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/api/dashboard/${slug}`;
}

// ---------------------------------------------------------------------------
// Card spec
// ---------------------------------------------------------------------------

/**
 * Content-based React key. Two cards sharing the same clipName + kind get
 * the same key across phases, so React reuses the same DOM element instead
 * of remounting — letting CSS animations (e.g. the RENDU comet) keep running
 * uninterrupted through a swipe.
 */
function cardKey(spec: CardSpec, slotFallback: string): string {
  if (spec.empty) return `empty-${slotFallback}`;
  return `${spec.kind ?? 'prise'}-${spec.clipName ?? slotFallback}`;
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

export function App() {
  const slug = useSlug();
  if (!slug) return <NoSlug />;
  return <Dashboard slug={slug} />;
}

function CenteredMessage({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-screen items-center justify-center p-8">
      <div className="max-w-md text-center">{children}</div>
    </main>
  );
}

function NoSlug() {
  return (
    <CenteredMessage>
      <h1 className="text-2xl font-bold">Timer Manager — Dashboard</h1>
      <p className="mt-4 text-neutral-400">
        Aucune chaîne sélectionnée. Ouvrez cette page avec une URL de la forme
        <code className="mx-2 rounded bg-neutral-800 px-2 py-0.5 font-mono text-sm">
          /dashboard/&lt;slug&gt;
        </code>
      </p>
    </CenteredMessage>
  );
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

function Dashboard({ slug }: { slug: string }) {
  const [channel, setChannel] = useState<DashboardChannelInfo | null>(null);
  const [collectorConnected, setCollectorConnected] = useState(false);
  const [otavConnected, setOtavConnected] = useState(false);
  // "No REST failure detected" until the collector says otherwise — an older
  // server omits the field, and that must not turn the dot red.
  const [otavRestOk, setOtavRestOk] = useState(true);
  const [ntpSynced, setNtpSynced] = useState(false);
  const [anchor, setAnchor] = useState<TimingAnchor | null>(null);
  const [fatalError, setFatalError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const wsUrl = useMemo(() => buildWsUrl(slug), [slug]);

  const onMessage = useCallback((msg: CloudToDashboardMessage) => {
    switch (msg.type) {
      case 'snapshot':
        setChannel(msg.channel);
        setCollectorConnected(msg.collector_connected);
        setOtavConnected(msg.otav_connected);
        setOtavRestOk(msg.otav_rest_ok ?? true);
        setNtpSynced(msg.ntp_synced);
        if (msg.data) setAnchor(makeAnchor(msg.data));
        break;
      case 'timing_update':
        setAnchor(makeAnchor(msg.data));
        break;
      case 'live_status_changed':
        break;
      case 'collector_status':
        setCollectorConnected(msg.connected);
        if (!msg.connected) {
          setOtavConnected(false);
          setNtpSynced(false);
        }
        break;
      case 'otav_status':
        setOtavConnected(msg.connected);
        break;
      // Deliberately NOT reset on collector disconnect, unlike the two above:
      // the server leaves REST health untouched then, and only emits on change.
      // Resetting it here would desync from a server still holding `false`,
      // and the collector's resend on reconnect would be deduplicated away.
      case 'otav_rest_status':
        setOtavRestOk(msg.rest_ok);
        break;
      case 'ntp_status':
        setNtpSynced(msg.synced);
        break;
    }
  }, []);

  const status = useWebSocket({ url: wsUrl, onMessage });

  useEffect(() => {
    if (status === 'closed' && !channel) {
      const t = setTimeout(() => {
        if (!channel) setFatalError('Chaîne introuvable ou serveur indisponible.');
      }, 3_000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [status, channel]);

  const transmissionDelayMs = channel?.transmission_delay_ms ?? 0;
  const processingOffsetMs = channel?.processing_offset_ms ?? 0;
  // Default to Europe/Paris until the snapshot arrives — avoids one render of
  // the Clock with an undefined timezone (which would crash Intl.DateTimeFormat).
  const displayTimezone = channel?.display_timezone ?? DEFAULT_DISPLAY_TIMEZONE;
  const interp = useInterpolatedTiming(anchor, transmissionDelayMs + processingOffsetMs);

  // Closes over `displayTimezone` so we don't have to thread it through every
  // call site. Re-created each render — cheap, no memo needed.
  const renderCard = (spec: CardSpec): ReactNode => {
    if (spec.empty) {
      return (
        <CountdownCard
          empty
          label=""
          emptyLabel={spec.emptyLabel}
          timeZone={displayTimezone}
        />
      );
    }
    return (
      <CountdownCard
        label={spec.label ?? ''}
        kind={spec.kind}
        clipName={spec.clipName}
        seconds={spec.seconds}
        scheduledAt={spec.scheduledAt}
        timeZone={displayTimezone}
        urgency={spec.urgency}
      />
    );
  };

  // Fullscreen
  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void document.documentElement.requestFullscreen();
    }
  }, []);
  useEffect(() => {
    const listener = (): void => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', listener);
    return () => document.removeEventListener('fullscreenchange', listener);
  }, []);

  if (fatalError) {
    return (
      <CenteredMessage>
        <h1 className="text-2xl font-bold text-red-400">Erreur</h1>
        <p className="mt-4 text-neutral-400">{fatalError}</p>
        <p className="mt-4 text-sm text-neutral-600">Slug : {slug}</p>
      </CenteredMessage>
    );
  }

  // --- Derived live state ---
  const isLive = interp?.isLive ?? false;
  const synthActive = interp?.syntheticRenduSeconds !== undefined;
  const itemExpired = interp?.itemRemainingExpired ?? false;

  const presenterLive = synthActive || (isLive && !itemExpired);
  const onAirVisible = presenterLive;
  const renduClipName = synthActive ? interp?.syntheticRenduName : interp?.itemDisplayName;
  const renduSeconds = synthActive ? interp?.syntheticRenduSeconds : interp?.itemRemainingSeconds;

  const hasNextLive =
    (interp?.nextLiveCardVisible ?? false) && !!interp?.nextLiveDisplayName;

  // --- Live card specs (what the left/right slots should show right now) ---
  //
  // Left slot = most urgent "live" card :
  //   - RENDU if a live is in progress (synthetic or real),
  //   - else PRISE if a next live is announced,
  //   - else empty.
  // Right slot = queued next :
  //   - PRISE only when there's already an active live AND a next one,
  //   - else empty, with a label that reflects what we actually know
  //     (see deriveRightSlot in utils/cards.ts).
  const renduSpec: CardSpec = {
    empty: false,
    kind: 'rendu',
    label: 'Fin du direct dans',
    clipName: renduClipName,
    seconds: renduSeconds,
    urgency: getUrgency(renduSeconds),
  };
  const priseSpec: CardSpec | null = hasNextLive
    ? {
        empty: false,
        kind: 'prise',
        label: 'Prochain direct dans',
        clipName: interp?.nextLiveDisplayName,
        seconds: interp?.nextLiveRemainingSeconds,
        scheduledAt: interp?.nextLiveAt,
        urgency: getUrgency(interp?.nextLiveRemainingSeconds),
      }
    : null;

  const liveLeft: CardSpec = deriveLeftSlot(presenterLive, renduSpec, priseSpec);
  // What comes after the left card: the N+2 info card, "no other live in
  // this playlist", or "not communicated". `nextLiveAnnounced` is read from the
  // DATA, not from `hasNextLive` — during the ~7 s window after a live starts
  // on the presenter's side (state 2b), the PRISE card has expired but the
  // anchor still names that live, and its following live is known.
  //
  // Exclusivity with the chained pre-RENDU (ribbon index 2) is structural:
  // isChained requires presenterLive && hasNextLive, i.e. a live PRISE, and
  // deriveRightSlot returns that PRISE before ever reaching the info card.
  const liveRight: CardSpec = deriveRightSlot({
    presenterLive,
    priseSpec,
    nextLiveAnnounced: !!interp?.nextLiveDisplayName,
    following: interp?.followingLive ?? { kind: 'unknown' },
  });
  // REQUIRED, not cosmetic. The enter class is recomputed on every render,
  // and liveRight carries animateEnter when stable but not while holding or
  // swiping. At the end of a swipe it keeps its key while animateEnter flips
  // false→true: the class lands on an already-mounted node and the fade+scale
  // replays exactly as the card finishes sliding in. The info card, which
  // typically arrives BY that swipe, must never do that.
  const rightAnimates = !liveRight.empty && liveRight.kind !== 'info';

  // --- Chained (back-to-back) detection ---
  //
  // Two lives chain when the current RENDU and the next PRISE share the
  // same end moment. In that case we render a hidden 3rd card (the RENDU
  // of the upcoming live) off-screen right. On trigger, the ribbon shifts
  // by TWO slots so that 3rd card becomes the new left card.
  const isChained =
    presenterLive &&
    hasNextLive &&
    !synthActive &&
    typeof interp?.itemRemainingSeconds === 'number' &&
    typeof interp?.nextLiveRemainingSeconds === 'number' &&
    typeof interp?.nextLiveDurationSeconds === 'number' &&
    interp.itemRemainingSeconds > 0 &&
    Math.abs(interp.itemRemainingSeconds - interp.nextLiveRemainingSeconds) < 2;

  const preRenduSpec: CardSpec | null =
    isChained &&
    interp?.nextLiveDisplayName &&
    typeof interp?.nextLiveDurationSeconds === 'number'
      ? {
          empty: false,
          kind: 'rendu',
          label: 'Fin du direct dans',
          clipName: interp.nextLiveDisplayName,
          seconds: interp.nextLiveDurationSeconds,
          urgency: getUrgency(interp.nextLiveDurationSeconds),
        }
      : null;

  // --- Carousel orchestration ---
  //
  // A swipe fires on two triggers :
  //   A) PRISE countdown hits 0    (next live activates — back-to-back case)
  //   B) RENDU countdown hits 0    (current live ends with a PRISE already
  //                                 queued — non-back-to-back case)
  //   C) Both hit 0 together        (chained lives — 2-slot shift)
  //
  // Animation :
  //   1. HOLD  (2s) : outgoing card(s) frozen at 00:00:00 on the left.
  //   2. SWIPE (N × 1s, N = shiftSlots) : ribbon shifts N slots left.
  //   3. STABLE : back to the standard live layout.
  //
  // Snapshot captures the frozen outgoing cards + shiftSlots. Incoming
  // cards are computed LIVE from the current state so countdowns (incl.
  // the new RENDU) keep ticking through hold+swipe.
  const prevLiveLeftRef = useRef<CardSpec>(liveLeft);
  const prevLiveRightRef = useRef<CardSpec>(liveRight);
  const prevIsChainedRef = useRef<boolean>(isChained);
  const latestLiveLeftRef = useRef<CardSpec>(liveLeft);
  const latestLiveRightRef = useRef<CardSpec>(liveRight);
  const latestIsChainedRef = useRef<boolean>(isChained);
  useEffect(() => {
    prevLiveLeftRef.current = latestLiveLeftRef.current;
    prevLiveRightRef.current = latestLiveRightRef.current;
    prevIsChainedRef.current = latestIsChainedRef.current;
    latestLiveLeftRef.current = liveLeft;
    latestLiveRightRef.current = liveRight;
    latestIsChainedRef.current = isChained;
  });

  interface RibbonSnapshot {
    frozenCards: CardSpec[];
    shiftSlots: number;
  }

  const makeSnapshot = useCallback<() => RibbonSnapshot>(() => {
    const prevLeft = prevLiveLeftRef.current;
    const prevRight = prevLiveRightRef.current;
    if (prevIsChainedRef.current) {
      // Chained : freeze both RENDU and PRISE at 0, shift 2 slots.
      // The 3rd ribbon card (preRendu) is replaced by live liveLeft post-
      // trigger, so it keeps counting during hold + swipe.
      return {
        frozenCards: [
          { ...prevLeft, seconds: 0 },
          { ...prevRight, seconds: 0 },
        ],
        shiftSlots: 2,
      };
    }
    // Case A / B : only the outgoing left card is frozen. The becoming
    // card is naturally produced as the new liveLeft at render time.
    return {
      frozenCards: [{ ...prevLeft, seconds: 0 }],
      shiftSlots: 1,
    };
  }, []);

  // Gate renduSeconds on `isLive || synthActive` (not presenterLive, which
  // also requires `!itemExpired`) so the trigger survives the brief tick
  // where itemRemainingSeconds reaches 0 but itemExpired has just flipped.
  const renduCountdownForCarousel =
    synthActive || isLive ? renduSeconds : undefined;

  const { phase, snapshot } = useCarouselPhase<RibbonSnapshot>(
    interp?.nextLiveRemainingSeconds,
    renduCountdownForCarousel,
    makeSnapshot,
    (snap) => snap.shiftSlots * CAROUSEL_SWIPE_MS,
  );

  // Ribbon content :
  //   - stable (normal)   : [liveLeft, liveRight]                (2 cards)
  //   - stable (chained)  : [liveLeft, liveRight, preRendu]      (3 cards,
  //                         3rd hidden off-screen right by overflow)
  //   - hold / swiping    : [...frozenCards, liveLeft, liveRight] (3 or 4)
  //
  // During hold the translateX stays at 0 so frozen cards occupy the
  // visible slots. During swiping the ribbon shifts by shiftSlots so
  // the live cards move into the visible slots.
  let ribbonCards: { key: string; spec: CardSpec; animateEnter?: boolean }[];
  if (snapshot === null) {
    ribbonCards = [
      { key: cardKey(liveLeft, 'left'), spec: liveLeft },
      {
        key: cardKey(liveRight, 'right'),
        spec: liveRight,
        animateEnter: rightAnimates,
      },
    ];
    if (isChained && preRenduSpec) {
      ribbonCards.push({ key: cardKey(preRenduSpec, 'pre'), spec: preRenduSpec });
    }
  } else {
    // Frozen cards keep their content-based key so the stable→hold transition
    // reuses the same DOM element — preserves CSS animation state (the RENDU
    // comet rotation), which would otherwise restart from 0° on remount.
    ribbonCards = snapshot.frozenCards.map((spec, i) => ({
      key: cardKey(spec, `frozen-${i}`),
      spec,
    }));
    // Live cards keep their content-based key too, so a card migrating from
    // a stable slot into a hold slot (e.g. PRISE in slot "right" → slot
    // "live-left" after the RENDU ends) reuses the same DOM element and
    // doesn't replay .animate-card-enter. Only prefix the key when it would
    // collide with a frozen card (chained case: synthetic RENDU shares the
    // frozen RENDU's clipName).
    const frozenKeys = new Set(ribbonCards.map((c) => c.key));
    const liveLeftKey = cardKey(liveLeft, 'live-left');
    const liveRightKey = cardKey(liveRight, 'live-right');
    ribbonCards.push({
      key: frozenKeys.has(liveLeftKey) ? `hold-live-left:${liveLeftKey}` : liveLeftKey,
      spec: liveLeft,
      animateEnter: !liveLeft.empty,
    });
    ribbonCards.push({
      key: frozenKeys.has(liveRightKey) ? `hold-live-right:${liveRightKey}` : liveRightKey,
      spec: liveRight,
    });
  }

  // Pulse-phase CSS var — aligns 2s animations with wall-clock seconds.
  const pulsePhaseStyle = useMemo<CSSProperties>(
    () =>
      ({
        '--pulse-phase': `-${Date.now() % 2000}ms`,
      }) as CSSProperties,
    [],
  );

  // --- Ribbon rendering ---
  //
  // Container shows a window of exactly 2 slots (on md+). The flex strip
  // inside holds 2 cards in `stable`, or 3 cards during `hold`/`swiping`.
  //
  // During `swiping`, translateX shifts by -(slotWidth + gap) with a CSS
  // transition so the leftmost card scrolls off and slots [1,2] become
  // the visible pair.
  //
  // On mobile (< md) we stack vertically without any slide animation —
  // we simply show the 2 cards that would be visible at each phase.
  const slotWidthDesktop = 'calc(50% - 0.75rem)'; // 50% - gap/2 (gap = 1.5rem = md:gap-6)
  const activeShift =
    phase === 'swiping' && snapshot !== null ? snapshot.shiftSlots : 0;
  // Shift of one slot expressed against the ribbon's bounding box. `50%` in
  // translateX resolves against the element's bounding box (including its
  // px-6 padding), not its content-box — so we can't just reuse
  // `slotWidthDesktop + gap` here (that formula is calibrated for `width`,
  // which resolves against content-box, and would overshoot by `padding/2`
  // = 24px per slot).
  const ribbonTransform = `translateX(calc(-1 * ${activeShift} * (50% - 0.75rem)))`;
  const swipeDurationMs =
    snapshot !== null ? snapshot.shiftSlots * CAROUSEL_SWIPE_MS : CAROUSEL_SWIPE_MS;
  const ribbonTransition =
    phase === 'swiping'
      ? `transform ${swipeDurationMs}ms ease-in-out`
      : 'none';

  // Mobile pair : during stable, liveLeft/liveRight ; during hold, the
  // frozen pre-swipe pair (1 frozen + becoming for case A/B, or 2 frozen
  // for chained) ; during swiping, the target pair (liveLeft/liveRight).
  // A missing frozen card falls back to the live one rather than crashing
  // renderCard — the snapshot always holds them, this only replaces `!`.
  const mobilePair: [CardSpec, CardSpec] =
    snapshot === null
      ? [liveLeft, liveRight]
      : phase === 'swiping'
        ? [liveLeft, liveRight]
        : snapshot.shiftSlots >= 2
          ? [snapshot.frozenCards[0] ?? liveLeft, snapshot.frozenCards[1] ?? liveRight]
          : [snapshot.frozenCards[0] ?? liveLeft, liveLeft];

  return (
    <main
      style={pulsePhaseStyle}
      className={`relative flex min-h-screen flex-col bg-neutral-950 p-6 md:p-10 ${
        onAirVisible ? 'on-air-viewport' : ''
      }`}
    >
      <div className="mx-auto flex w-full max-w-[1600px] flex-1 flex-col">
        <header className="relative">
          <div className="flex items-start justify-between gap-4">
            {channel ? (
              <ChannelLogo
                logoPath={channel.logo_path}
                logoInvert={channel.logo_invert}
                name={channel.name}
              />
            ) : (
              <div className="text-xl font-semibold">{slug}</div>
            )}
            <Clock now={interp?.now} timeZone={displayTimezone} />
          </div>
          <div
            className={`mt-6 flex justify-center md:absolute md:inset-y-0 md:left-1/2 md:mt-0 md:-translate-x-1/2 md:items-center ${
              onAirVisible ? '' : 'hidden md:flex'
            }`}
          >
            <OnAirBadge visible={onAirVisible} />
          </div>
        </header>

        {/* Mobile: simple 2-row stack, no ribbon (no horizontal space) */}
        <section className="my-auto grid grid-cols-1 gap-5 py-8 md:hidden">
          <div>{renderCard(mobilePair[0])}</div>
          <div
            key={cardKey(mobilePair[1], 'mobile-right')}
            className={
              // Stable phase ⟺ snapshot === null ⟺ mobilePair[1] is liveRight.
              snapshot === null && rightAnimates ? 'animate-card-enter' : ''
            }
          >
            {renderCard(mobilePair[1])}
          </div>
        </section>

        {/* Desktop: horizontal ribbon with 2 visible slots.
            The overflow-hidden wrapper extends 24px past each card's outer
            edge (same distance as the gap between cards) — so cards fade out
            one "gap width" past their stable position when sliding. */}
        <section className="my-auto hidden py-8 md:block">
          <div
            className="-mx-6 overflow-hidden"
            style={{
              maskImage:
                'linear-gradient(to right, transparent 0, black 1rem, black calc(100% - 1rem), transparent 100%)',
              WebkitMaskImage:
                'linear-gradient(to right, transparent 0, black 1rem, black calc(100% - 1rem), transparent 100%)',
            }}
          >
            <div
              className="mx-auto flex gap-6 px-6"
              style={{
                transform: ribbonTransform,
                transition: ribbonTransition,
                willChange: 'transform',
              }}
            >
              {ribbonCards.map((card) => (
                <div
                  key={card.key}
                  className={`shrink-0 ${card.animateEnter ? 'animate-card-enter' : ''}`}
                  style={{ width: slotWidthDesktop }}
                >
                  {renderCard(card.spec)}
                </div>
              ))}
            </div>
          </div>
        </section>

        <footer className="flex flex-col items-center gap-4 text-neutral-500 md:grid md:grid-cols-3">
          <div className="flex items-center gap-4 md:justify-self-start">
            <StatusIndicator
              otavConnected={otavConnected}
              otavRestOk={otavRestOk}
              collectorConnected={collectorConnected}
              clientConnected={status === 'open'}
              ntpSynced={ntpSynced}
            />
            <SourceIndicator source={interp?.activeSource} />
          </div>

          <img
            src={`${import.meta.env.BASE_URL}LOGO-CaptaCenter-RVB-blanc.png`}
            alt="CaptaCenter"
            className="h-10 w-auto opacity-80 md:h-10 md:justify-self-center"
          />

          <div className="flex flex-wrap items-center justify-center gap-3 md:justify-end">
            {transmissionDelayMs > 0 && (
              <span className="text-[10px] uppercase tracking-widest">
                Délai transmission {(transmissionDelayMs / 1000).toFixed(1)}s
              </span>
            )}
            <button
              type="button"
              onClick={toggleFullscreen}
              className="rounded border border-neutral-800 px-3 py-1 text-[10px] uppercase tracking-widest text-neutral-400 transition hover:border-neutral-600 hover:text-neutral-200"
            >
              {isFullscreen ? 'Quitter plein écran' : 'Plein écran'}
            </button>
          </div>
        </footer>
      </div>
    </main>
  );
}
